import React from 'react';
import { colors, spacing, typography, typographyFlat } from '../../styles/tokens';
import { PriceHistoryChart, PriceHistoryPoint } from '../pricing/PriceHistoryChart';
import { parseInstant } from '../../utils/datetime';

export interface ManapoolSalePoint {
  sold_at?: string | null;
  price_cents: number;
  quantity?: number;
}

interface ManapoolSalesChartProps {
  sales?: ManapoolSalePoint[] | null;
  height?: number;
}

function velocitySummary(sales: ManapoolSalePoint[]): string | null {
  if (!sales.length) return null;
  const withDates = sales.filter((s) => s.sold_at);
  const totalQty = sales.reduce((sum, s) => sum + (s.quantity ?? 1), 0);
  const avgCents =
    sales.reduce((sum, s) => sum + s.price_cents * (s.quantity ?? 1), 0) / Math.max(totalQty, 1);

  if (withDates.length < 2) {
    return `${totalQty} sale${totalQty === 1 ? '' : 's'} · avg $${(avgCents / 100).toFixed(2)}`;
  }

  const times = withDates
    .map((s) => parseInstant(s.sold_at)?.getTime())
    .filter((t): t is number => t != null)
    .sort((a, b) => a - b);
  if (times.length < 2) {
    return `${totalQty} sale${totalQty === 1 ? '' : 's'} · avg $${(avgCents / 100).toFixed(2)}`;
  }
  const spanMs = times[times.length - 1] - times[0];
  const days = Math.max(1, Math.round(spanMs / (24 * 60 * 60 * 1000)));
  return `${totalQty} sales in ${days} day${days === 1 ? '' : 's'} · avg $${(avgCents / 100).toFixed(2)}`;
}

/**
 * Manapool marketplace comps chart (price trend + velocity).
 * Hidden when there are no sales.
 */
export function ManapoolSalesChart({ sales, height = 120 }: ManapoolSalesChartProps) {
  if (!sales || sales.length === 0) {
    return null;
  }

  const data: PriceHistoryPoint[] = sales
    .filter((s) => s.price_cents != null && s.price_cents > 0)
    .map((s) => ({
      timestamp: s.sold_at || '',
      price: s.price_cents / 100,
    }));

  if (data.length === 0) {
    return null;
  }

  const summary = velocitySummary(sales);

  return (
    <div style={wrapStyle}>
      <PriceHistoryChart data={data} height={height} title="Manapool sales" />
      {summary && <div style={velocityStyle}>{summary}</div>}
    </div>
  );
}

const wrapStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.xs,
  marginTop: spacing.sm,
};

const velocityStyle: React.CSSProperties = {
  fontSize: typography.small.fontSize,
  color: colors.textSecondary,
  fontWeight: typographyFlat.fontWeight.medium,
};
